import {Component, OnInit, ViewChild} from '@angular/core';
import {UtilsService} from '../../../../appSharedModule/services/utils.service';
import {DadService} from '../../../services/dad.service';
import {pluck} from 'rxjs/operators';
import {AuthenticationService} from '../../../../authenticationModule/services/authentication.service';
import {formatDate, registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {ConfirmationService} from 'primeng/api';
import {CsvService} from '../../../services/csv.service';
import {DadItemReport} from '../../../models/dadItemReport';
import {BaseClientComponent} from '../../../../components/base-client.component';
import {HttpClient} from '@angular/common/http';
import {DadFormsListComponent} from '../dad-forms-list/dad-forms-list.component';

registerLocaleData(localeFr);

@Component({
  selector: 'app-dad-admin',
  templateUrl: './dad-admin.component.html',
  styleUrls: ['./dad-admin.component.scss']
})
export class DadAdminComponent extends BaseClientComponent implements OnInit {
  @ViewChild(DadFormsListComponent) child: DadFormsListComponent;
  exchangesNames = [];
  reportingEmail: string;
  technicalIssueReportingEmail: string;
  errorCsvReport = false;
  errorMessageCsvReport = '';
  generatedDadsMessage: boolean;
  exchangeNamesList = [];
  availableCompanyNames = [];
  sendRemindersMessage = false;

  constructor(protected utilsService: UtilsService,
              private dadsService: DadService,
              public authenticationService: AuthenticationService,
              private confirmationService: ConfirmationService,
              private csvService: CsvService,
              protected http: HttpClient) {
    super();
  }

  ngOnInit() {
    this.utilsService.lockUi();
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.initData();
    });
    this.subscriptions.push(response$);
  }

  private initData() {
    this.dadsService.getReportingEmails().pipe(pluck('market_data_email')).subscribe(val => this.reportingEmail = String(val));
    this.dadsService.getReportingEmails().pipe(pluck('quincy_data_email')).subscribe(val => this.technicalIssueReportingEmail = String(val));
    this.dadsService.getExchanges().subscribe(
      res => {
        this.exchangesNames = res;
        this.exchangeNamesList = res.map(x => ({
          label: x,
          value: x
        }));
        this.utilsService.unlockUi();
      }
    );
  }

  generateDads() {
    this.confirmationService.confirm({
      key: 'generateDadsCD',
      header: 'Generate DADs',
      message: 'This action will generate new DADs (if not already created for the current usage month). ' +
        'It may also send emails to clients.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Generate',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-outlined p-button-danger',
      defaultFocus: 'reject',
      accept: () => {
        this.dadsService.generateDads().subscribe({
          next: () => {
            this.child.getDadsList();
            this.generatedDadsMessage = true;
          },
          error: () => {
            this.utilsService.displayMessage('The DADs could not be generated', 'Please try again later.', 'error');
          }
        });
      }
    });
  }

  sendSecondReminder() {
    this.confirmationService.confirm({
      key: 'sendRemindersCD',
      header: 'Send Reminder Emails',
      message: 'This will send emails to all the clients who have not yet filled in their DAD forms.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Send reminders',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-outlined p-button-warning',
      defaultFocus: 'reject',
      accept: () => {
        this.dadsService.sendReminders().subscribe({
          next: () => {
            this.sendRemindersMessage = true;
          },
          error: () => {
            this.utilsService.displayMessage('The reminder emails could not be sent', 'Please try again later.', 'error');
          }
        });
      }
    });
  }

  isMckayUser(): boolean {
    return this.authenticationService.userCurrent.permissions.isMckayUser;
  }

  exportDadItemsReport() {
    this.dadsService.dadItemsReport().subscribe({
      next: reportItems => {
        const csv = this.generateCsv(reportItems);
        this.csvService.downloadCSV(csv, formatDate(new Date(), 'yyyy-MM-dd', 'en-us') + '_dad_items_report.csv');
      },
      error: () => {
        this.utilsService.displayMessage('The market data item list could not be exported', 'Please try again later.', 'error');
      }
    });
  }

  generateCsv(reportItems: DadItemReport[]) {
    const csvCols = [
      {field: 'companyName', header: 'Client Name'},
      {field: 'billingAccountName', header: 'Billing account name'},
      {field: 'productName', header: 'Product'},
      {field: 'marketsNames', header: 'Markets'},
      {field: 'exchangeName', header: 'Exchange'},
      {field: 'crossConnectId', header: 'Cross connect id'},
      {field: 'status', header: 'status'},
      {field: 'service', header: 'service'},
      {field: 'technicalPopId', header: 'Technical pop id'},
      {field: 'commercialPopId', header: 'Commercial pop id'},
      {field: 'activeOrderForm', header: 'Order form id'},
      {field: 'isProtected', header: 'Is protected'},
      {field: 'isArbitration', header: 'Is arbitration'},
      {field: 'isEncrypted', header: 'Is encrypted'},
      {field: 'configuredDate', header: 'Configured Date'},
      {field: 'deconfiguredDate', header: 'Deconfigured Date'},
      {field: 'trialStartDate', header: 'Trial start date'},
      {field: 'trialEndDate', header: 'Trial end date'},
      {field: 'selfHosted', header: 'Self hosted'},
      {field: 'nameThirdPartyHost', header: 'Name third party host'},
      {field: 'createdDate', header: 'Created date'},
      {field: 'createdBy', header: 'Created by'},
      {field: 'updatedDate', header: 'Updated date'},
      {field: 'updatedBy', header: 'Updated by'}
    ];

    let csv = '\ufeff';
    for (let i = 0; i < csvCols.length; i++) {
      csv += '"' + csvCols[i].header + '"';
      if (i < (csvCols.length - 1)) {
        csv += ',';
      }
    }
    for (const item of reportItems) {
      csv = this.csvService.addCsvLine(csv, csvCols, item);
    }
    return csv;
  }

  alterErrorCsvReport($event) {
    this.errorCsvReport = $event;
  }

  setAvailableCompanyNames($event: any) {
    this.availableCompanyNames = $event;
  }
}
